import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Star, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { publicApi } from '../../lib/api';
import { formatPrice, categoryColors } from '../../lib/utils';
import { cn } from '../../lib/utils';

export function FeaturedPackages() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    loadTours();
  }, []);

  const loadTours = async () => {
    try {
      const response = await publicApi.getFeaturedTours();
      setTours(response.data.slice(0, 8));
    } catch (error) {
      console.error('Error loading tours:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 bg-white" data-testid="featured-packages">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-3">
            ✨ Popular Destinations
          </span>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Featured Tour Packages
          </h2>
          <p className="text-[#4A4A4A] max-w-2xl mx-auto text-lg">
            Explore our handpicked selection of extraordinary destinations, 
            crafted to create unforgettable memories.
          </p>
        </div>

        {/* Tours Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden">
                <Skeleton className="h-52 w-full" />
                <div className="p-4 space-y-3">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : tours.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tours.map((tour, index) => (
              <Link
                key={tour.id}
                to={`/tours/${tour.slug}`}
                className={cn(
                  'group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500',
                  'transform hover:-translate-y-2'
                )}
                onMouseEnter={() => setHoveredId(tour.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`featured-tour-${tour.slug}`}
              >
                {/* Image Container */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={tour.images?.[0] || 'https://images.pexels.com/photos/2325446/pexels-photo-2325446.jpeg'}
                    alt={tour.name}
                    className={cn(
                      'w-full h-full object-cover transition-transform duration-700',
                      hoveredId === tour.id ? 'scale-110' : 'scale-100'
                    )}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Badges */}
                  <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
                    <Badge className={cn(categoryColors[tour.category] || 'bg-gray-100', 'shadow-lg')}>
                      {tour.category}
                    </Badge>
                    {tour.featured && (
                      <Badge className="bg-[#FF6B35] text-white shadow-lg">
                        🔥 Hot
                      </Badge>
                    )}
                  </div>

                  {/* Price Tag */}
                  <div className="absolute bottom-3 right-3">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg">
                      {tour.discounted_price && (
                        <span className="text-xs text-[#4A4A4A] line-through block">
                          {formatPrice(tour.price)}
                        </span>
                      )}
                      <span className="text-lg font-bold text-[#004E64]">
                        {formatPrice(tour.discounted_price || tour.price)}
                      </span>
                    </div>
                  </div>

                  {/* Discount Badge */}
                  {tour.discounted_price && (
                    <div className="absolute bottom-3 left-3">
                      <Badge className="bg-[#2A9D8F] text-white">
                        {Math.round(((tour.price - tour.discounted_price) / tour.price) * 100)}% OFF
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  {/* Location */}
                  <div className="flex items-center gap-1 text-[#FF6B35] text-sm mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>{tour.destination}</span>
                  </div>

                  {/* Title */}
                  <h3 
                    className="text-lg font-semibold text-[#1A1A1A] mb-3 group-hover:text-[#FF6B35] transition-colors line-clamp-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {tour.name}
                  </h3>

                  {/* Meta Info */}
                  <div className="flex items-center justify-between text-sm text-[#4A4A4A] pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{tour.duration_days}D / {tour.duration_nights}N</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-[#E9C46A] text-[#E9C46A]" />
                      <span>4.8</span>
                    </div>
                  </div>
                </div>

                {/* Hover Overlay */}
                <div className={cn(
                  'absolute inset-0 bg-[#004E64]/90 flex items-center justify-center transition-opacity duration-300',
                  hoveredId === tour.id ? 'opacity-100' : 'opacity-0 pointer-events-none'
                )}>
                  <Button 
                    className="bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-full px-6"
                  >
                    View Details <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-gray-50 rounded-2xl">
            <p className="text-[#4A4A4A] text-lg">No featured tours available yet.</p>
            <Link to="/contact" className="text-[#FF6B35] font-medium mt-2 inline-block hover:underline">
              Contact us for custom packages
            </Link>
          </div>
        )}

        {/* View All Button */}
        <div className="text-center mt-12">
          <Link to="/tours">
            <Button 
              variant="outline" 
              size="lg"
              className="border-[#004E64] text-[#004E64] hover:bg-[#004E64] hover:text-white rounded-full px-8 group"
            >
              View All Packages
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
